import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatBRL, formatDate } from '@/pages/painel/lib/radarConstants';
import {
  ObraEnriquecida,
  NATUREZA_OBRA_LABELS,
  MOTIVO_DESCARTE_OBRA_OPCOES,
  MOTIVO_DESCARTE_OBRA_LABELS,
  TRIAGEM_FAIXAS,
} from '@/pages/painel/lib/triagem';

function faixaInfo(key: string) {
  return TRIAGEM_FAIXAS.find((f) => f.key === key) ?? TRIAGEM_FAIXAS[TRIAGEM_FAIXAS.length - 1];
}

function formatArea(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} m²`;
}

export function TriagemDetailModal({
  obra,
  onClose,
  onCriarOportunidade,
  criando,
  onMonitorar,
  monitorando,
  onDescartar,
  onReabrir,
}: {
  obra: ObraEnriquecida;
  onClose: () => void;
  onCriarOportunidade: () => void;
  criando: boolean;
  onMonitorar: () => void;
  monitorando: boolean;
  onDescartar: () => void;
  onReabrir: () => void;
}) {
  const faixa = faixaInfo(obra.triagem.faixa);
  const temOportunidade = !!obra.oportunidade;
  const decisao = obra.decisao;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-lg">
              <i className="ri-building-4-line"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm">{obra.contratante || `Obra ${obra.rrt_numero ?? ''}`}</h3>
              <p className="text-xs text-gray-500">RRT {obra.rrt_numero ?? '—'} · {obra.cidade ?? '—'}-{obra.uf ?? '—'}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Prioridade de triagem */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="flex items-center gap-2">
                <i className={`${faixa.icon} text-lg`}></i>
                <span className="font-bold text-gray-900">{faixa.label}</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">{obra.triagem.total}<span className="text-sm text-gray-400 font-medium">/100</span></span>
            </div>
            <div className="space-y-2">
              {obra.triagem.fatores.map((f) => (
                <div key={f.key} className="flex items-start gap-3 text-sm">
                  <span className="w-32 shrink-0 text-gray-500 text-xs whitespace-nowrap">{f.label}</span>
                  <div className="flex-1 min-w-0">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-[#E8610A] rounded-full" style={{ width: `${(f.pontos / f.max) * 100}%` }} />
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{f.motivo}</p>
                  </div>
                  <span className="w-14 shrink-0 text-right text-xs font-semibold text-gray-700 whitespace-nowrap">{f.pontos}/{f.max}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Dados da obra */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Natureza" value={NATUREZA_OBRA_LABELS[obra.triagem.natureza] ?? obra.triagem.natureza} />
            <Field label="Segmento" value={obra.segmento ?? '—'} />
            <Field label="Natureza técnica" value={obra.natureza_tecnica ?? '—'} />
            <Field label="Atividade (RRT)" value={obra.atividades_rrt ?? '—'} />
            <Field label="Área" value={formatArea(obra.area_max)} />
            <Field label="Valor de contrato técnico (CAU/RRT)" value={formatBRL(obra.valor_contrato_max)} />
            <Field label="Início" value={formatDate(obra.data_inicio)} />
            <Field label="Fim" value={formatDate(obra.data_fim)} />
            <Field label="Bairro" value={obra.bairro ?? '—'} />
            <Field label="Responsável" value={obra.profissional ?? '—'} />
            <Field label="Fonte" value={obra.fonte ?? '—'} />
            <Field label="Situação" value={obra.em_andamento ? 'Em andamento' : 'Não marcada como em andamento'} />
          </div>

          {obra.triagem.dataInconsistente && (
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
              <i className="ri-alert-line mt-0.5"></i>
              <span>
                <strong>Data inconsistente.</strong> {obra.triagem.dataInconsistenteMotivo}
              </span>
            </div>
          )}

          {obra.criterio_classificacao && (
            <div className="space-y-1">
              <p className="text-[11px] text-gray-500 uppercase font-semibold">Critério de classificação</p>
              <p className="text-sm text-gray-700 bg-amber-50/60 rounded-lg p-3">{obra.criterio_classificacao}</p>
            </div>
          )}

          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            O valor de contrato técnico é o honorário de responsabilidade técnica (RRT/CAU) — não representa valor da obra nem potencial comercial.
          </div>

          {/* Ações */}
          <div className="flex items-center gap-2 flex-wrap pt-1">
            {temOportunidade ? (
              <Link
                to={`/painel/radar/oportunidades/${obra.oportunidade!.id}`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-external-link-line"></i> OPORTUNIDADE CRIADA
              </Link>
            ) : (
              <button
                onClick={onCriarOportunidade}
                disabled={criando}
                className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
              >
                <i className={`${criando ? 'ri-loader-4-line animate-spin' : 'ri-radar-line'}`}></i>
                Criar oportunidade no Radar
              </button>
            )}
            {!temOportunidade && !decisao && (
              <button
                onClick={onMonitorar}
                disabled={monitorando}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#E8610A] bg-[#E8610A]/10 hover:bg-[#E8610A]/20 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
              >
                <i className={`${monitorando ? 'ri-loader-4-line animate-spin' : 'ri-eye-line'}`}></i>
                Monitorar
              </button>
            )}
            {!temOportunidade && decisao?.decisao !== 'descartar' && (
              <button
                onClick={onDescartar}
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-close-circle-line"></i> Descartar
              </button>
            )}
            {decisao && (
              <button
                onClick={onReabrir}
                className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-arrow-go-back-line"></i> Reabrir para análise
              </button>
            )}
          </div>

          {decisao && (
            <div className="text-sm border rounded-lg px-4 py-3 bg-gray-50 border-gray-200">
              <span className="font-semibold text-gray-700">
                {decisao.decisao === 'monitorar' ? 'Em monitoramento' : 'Descartada da triagem'}
              </span>
              {decisao.motivo && (
                <span className="text-gray-500"> · {MOTIVO_DESCARTE_OBRA_LABELS[decisao.motivo] ?? decisao.motivo}</span>
              )}
              {decisao.observacao && <p className="text-gray-500 mt-1">{decisao.observacao}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
      <p className="text-[11px] text-gray-500 uppercase font-semibold">{label}</p>
      <p className="text-sm font-semibold text-gray-900 mt-0.5 break-words">{value}</p>
    </div>
  );
}

export function TriagemDescarteModal({
  obra,
  onClose,
  onConfirm,
  saving,
}: {
  obra: ObraEnriquecida;
  onClose: () => void;
  onConfirm: (motivo: string, observacao: string) => void;
  saving: boolean;
}) {
  const [motivo, setMotivo] = useState('');
  const [observacao, setObservacao] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-lg shadow-xl">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900 text-sm">Descartar da triagem</h3>
            <p className="text-xs text-gray-500">{obra.contratante}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">Informe o motivo do descarte. A obra original <strong>não</strong> será apagada.</p>

          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {MOTIVO_DESCARTE_OBRA_OPCOES.map((m) => (
              <label
                key={m.key}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                  motivo === m.key ? 'border-[#E8610A] bg-[#E8610A]/5' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="motivo-descarte-obra"
                  value={m.key}
                  checked={motivo === m.key}
                  onChange={() => setMotivo(m.key)}
                  className="cursor-pointer"
                />
                <span className="text-sm text-gray-800">{m.label}</span>
              </label>
            ))}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Observação (opcional)</label>
            <textarea
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Detalhe o motivo..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] focus:ring-2 focus:ring-[#E8610A]/20 bg-white resize-none"
            />
            <p className="text-[11px] text-gray-400 text-right mt-1">{observacao.length}/500</p>
          </div>

          <div className="flex items-center gap-2 justify-end pt-1">
            <button
              onClick={onClose}
              className="text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={() => motivo && onConfirm(motivo, observacao)}
              disabled={!motivo || saving}
              className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
            >
              <i className={`${saving ? 'ri-loader-4-line animate-spin' : 'ri-check-line'}`}></i>
              {saving ? 'Descartando...' : 'Confirmar descarte'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}